import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["buttonType", "fluid", "size"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { BUTTON_SIZES, BUTTON_TYPES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { ButtonGroupContext } from './ButtonGroup.context';
import { StyledButtonGroup } from './ButtonGroup.styles';

/**
 * Be sure to add an appropriate
 * [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) for assistive technologies.
 *
 * @related ['Button', 'LoadingButton', 'TextButton', 'ToggleButton']
 * @since 5.0.0
 */
export var ButtonGroup = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var buttonType = _ref.buttonType,
    fluid = _ref.fluid,
    size = _ref.size,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(ButtonGroupContext.Provider, {
    value: {
      buttonType: buttonType,
      fluid: fluid,
      size: size
    }
  }, /*#__PURE__*/React.createElement(StyledButtonGroup, _extends({
    ref: forwardedRef,
    fluid: fluid
  }, rest)));
});
ButtonGroup.displayName = 'ButtonGroup';
ButtonGroup.propTypes = {
  /**
   * An [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) is required for assistive
   * technologies to announce the button group properly.
   */
  'aria-label': PropTypes.string.isRequired,
  /**
   * Sets `buttonType` for all `Button` children.
   */
  'buttonType': PropTypes.oneOf(Object.values(BUTTON_TYPES)),
  /**
   * Connect the buttons by removing the space between buttons in the group. In general, you will
   * only want to do this with buttons of the same type. You can use `buttonType` to set the type
   * to be the same for all buttons.
   */
  'conjoined': PropTypes.bool,
  /**
   * The direction in which the buttons should be aligned. Buttons will be fluid when the
   * direction is set to `column`.
   *
   * Supports inline media query objects
   */
  'direction': CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['row', 'row-reverse', 'column', 'column-reverse'])),
  /**
   * The button group will expand to fill its parent container.
   *
   * Supports inline media query objects
   */
  'fluid': CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * Sets the `role` of the button group. By default, this is set to
   * ["group"](https://www.w3.org/TR/wai-aria-1.2/#group), but "toolbar" might be more
   * appropriate if the button group is used in a toolbar.
   */
  'role': PropTypes.string,
  /**
   * Sets `size` for all `Button` children.
   */
  'size': PropTypes.oneOf(Object.values(BUTTON_SIZES))
};
ButtonGroup.defaultProps = {
  direction: 'row',
  role: 'group'
};